import { useEffect, useState, useCallback } from 'react';
import { useMijnWerkStore } from '@/stores/mijnWerk';
import { listCollectorPhotos } from '@/api/tasks';
import type { PhotoItem } from '@/types/odoo';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { isPdfPhoto } from '@/utils/photoFiles';
import type {
  MijnWerkCollector, MijnWerkGroup, MijnWerkMeerwerk, MijnWerkMelding,
  MijnWerkMinprijs, MijnWerkOther, MijnWerkPo, MijnWerkPoRef,
} from '@/api/mijnWerk';

// ── Formatters (exported for tests) ──────────────────────────────────────────

/** Belgian euro format: € 1.234,56 | −€ 22,00 */
export function formatEuro(amount: number): string {
  const abs = Math.abs(amount);
  const [intPart, decPart] = abs.toFixed(2).split('.');
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const prefix = amount < 0 ? '−€ ' : '€ ';
  return `${prefix}${intFormatted},${decPart}`;
}

const EN_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseDateLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** A PO line with no date still has to appear somewhere the crew can find it. */
export const NO_DATE_HEADING = 'Date not recorded';

function formatDayHeader(dateStr: string | null): string {
  if (!dateStr) return NO_DATE_HEADING;
  const d = parseDateLocal(dateStr);
  return `${EN_WEEKDAYS[d.getDay()]} ${d.getDate()} ${EN_MONTHS[d.getMonth()]}`;
}

/** "7 Sep 2026" */
export function formatShortDate(dateStr: string): string {
  const d = parseDateLocal(dateStr);
  return `${d.getDate()} ${EN_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Date span of the work on a PO: "17 – 30 Jun 2026" (same month) or
 * "25 Jun – 8 Jul 2026". A PO with no dated work yet has no span.
 */
export function formatPoSpan(po: { dateFrom: string | null; dateTo: string | null }): string {
  if (!po.dateFrom || !po.dateTo) return 'No dated work yet';
  return formatPeriodLabel(po.dateFrom, po.dateTo);
}

/** "17 – 30 Jun 2026" (same month) or "25 Jun – 8 Jul 2026" (cross-month) */
export function formatPeriodLabel(from: string, to: string): string {
  const f = parseDateLocal(from);
  const t = parseDateLocal(to);
  if (f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear()) {
    return `${f.getDate()} – ${t.getDate()} ${EN_MONTHS[t.getMonth()]} ${t.getFullYear()}`;
  }
  return `${f.getDate()} ${EN_MONTHS[f.getMonth()]} – ${t.getDate()} ${EN_MONTHS[t.getMonth()]} ${t.getFullYear()}`;
}

// ── English meerwerk type labels (subcontractor-facing) ──────────────────────

// Keys are the real Odoo x_meerwerk.x_type values (see meerwerken.ts MEERWERK_TYPES
// + the cockpit manual-line types). Unknown types fall back to the raw value.
const MEERWERK_LABELS: Record<string, string> = {
  wachturen: 'Working hours',
  kilometervergoeding: 'Travel',
  voertuigvergoeding: 'Vehicle',
  extra_materiaal: 'Extra material',
  collectoren_geplaatst: 'Collectors placed',
  overig: 'Other',
  binddraad: 'Binding wire',
};

// ── Desktop layout ────────────────────────────────────────────────────────────

/**
 * The phone layout is the design target and is left exactly as it is. Desktop
 * (lg+) changes only what a phone cannot do: the sheet gets wider, the statement
 * moves into a rail that stays in view while the lines scroll, every row is read
 * at desktop size, and a line's flag sits beside the row instead of under it —
 * so forty lines read as a statement instead of one long bar.
 */
const ROW_PAD = 'px-3 py-2.5 lg:px-4 lg:py-3';
const ROW_TITLE = 'text-[13px] lg:text-sm font-semibold';
const ROW_SUB = 'text-[11px] lg:text-xs text-td-muted';
const ROW_AMOUNT = 'text-[13px] lg:text-[15px] font-bold tabular-nums';
const ROW_RATE = 'text-[10px] lg:text-[11px] text-td-light tabular-nums';
/** Amount column: a fixed width on desktop, so every figure lines up down the card. */
const ROW_AMOUNT_COL = 'text-right shrink-0 lg:w-36';
/**
 * The chevron gutter. Only a tappable collector has an arrow, but on desktop every
 * row reserves the space — otherwise a tappable row's amount sits an arrow's width
 * left of the row above it and the column stops being a column. Phone-side nothing
 * changes: the empty slot is `hidden` below lg.
 */
const ROW_CHEVRON_SLOT = 'hidden lg:block w-4 shrink-0';
/** A row and its flag strip: stacked on a phone, side by side on desktop. */
const ROW_SHELL = 'lg:flex lg:items-start';
const ROW_BODY = 'lg:flex-1 lg:min-w-0';

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * The PO line's own text, shown under every row. The rest of a row is our reading
 * of the line — this is the line as the OA will see it on the purchase order, so a
 * hand-edited or renamed line cannot silently look like something else.
 */
function LineDescription({ text }: { text: string }) {
  if (!text.trim()) return null;
  return (
    <p className="text-[11px] lg:text-xs break-words text-td-light leading-snug whitespace-pre-line mt-0.5">{text}</p>
  );
}

// ── Flagging a line ───────────────────────────────────────────────────────────

/** The line a flag is about, as the sheet and the office will name it. */
export interface FlagTarget {
  poLineId: number;
  label: string;
}

interface RowFlagProps {
  /** The flag already on this line, open or resolved. */
  melding?: MijnWerkMelding;
  onFlag: (target: FlagTarget) => void;
}

/**
 * The strip under every billable row.
 *
 * Quiet until it is used: an unflagged row shows a plain grey link, because
 * forty of these must not turn a pay overview into a wall of buttons. A flagged
 * row shows a badge loud enough to find while scrolling.
 */
function FlagStrip({ melding, onOpen }: { melding?: MijnWerkMelding; onOpen: () => void }) {
  if (!melding) {
    return (
      <div className="px-3 pb-2 -mt-1 lg:mt-0 lg:w-44 xl:w-56 lg:shrink-0 lg:py-3 lg:pl-0">
        {/* Quiet, but still a 36px target — this is tapped with a work glove on. */}
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center gap-1 min-h-[36px] pr-2 text-[11px] text-td-light active:text-td-navy"
        >
          <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 21V4h11l-1 3h7v9h-8l-1-3H4" />
          </svg>
          <span className="underline underline-offset-2">Report an issue</span>
        </button>
      </div>
    );
  }

  const isOpen = melding.status === 'open';
  return (
    <div className="px-3 pb-2 -mt-1 lg:mt-0 lg:w-44 xl:w-56 lg:shrink-0 lg:py-3 lg:pl-0">
      <button
        type="button"
        onClick={onOpen}
        className={`w-full text-left rounded-lg px-2 py-1.5 ${isOpen ? 'bg-amber-50 active:bg-amber-100' : 'bg-emerald-50 active:bg-emerald-100'}`}
      >
        <span className={`text-[10px] font-bold uppercase tracking-wide ${isOpen ? 'text-amber-700' : 'text-emerald-700'}`}>
          {isOpen ? 'Reported · pending' : 'Reported · resolved'}
        </span>
        <span className="block text-[11px] text-td-muted whitespace-pre-line leading-snug mt-0.5 lg:line-clamp-3 break-words">
          {melding.text}
        </span>
        {melding.resolution && (
          <span className="block text-[11px] text-emerald-800 leading-snug mt-1 lg:line-clamp-2 break-words">
            Office: {melding.resolution}
          </span>
        )}
      </button>
    </div>
  );
}

/**
 * Pick the flag to show for a line. A line can carry a resolved flag and a fresh
 * open one at the same time; the open one is the state the OA is waiting on.
 */
export function meldingForLine(meldingen: MijnWerkMelding[], poLineId: number): MijnWerkMelding | undefined {
  const forLine = meldingen.filter(m => m.poLineId === poLineId);
  return forLine.find(m => m.status === 'open') ?? forLine[forLine.length - 1];
}

interface CollectorRowProps extends RowFlagProps {
  collector: MijnWerkCollector;
  tappable: boolean;
  onTap: (collector: MijnWerkCollector) => void;
}

function CollectorRow({ collector, tappable, onTap, melding, onFlag }: CollectorRowProps) {
  const isMulti = (collector.segments?.length ?? 0) > 1;
  // `tappable` means the caller is on the project, which is also what the photo
  // endpoint's scoping requires — a row we cannot open is a row whose photos the
  // server would refuse anyway.
  const canTap = tappable;

  // The PO line's own text is the row. The task-derived label only fills in when the
  // line has no text of its own — that label describes the task as it is named *today*,
  // which is not necessarily what this PO was written against.
  const heading = (!isMulti && collector.description.trim())
    ? collector.description
    : `${collector.label} · ${collector.blok} / ${collector.verdieping}`;

  const inner = (
    <>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className={`${ROW_TITLE} leading-snug break-words whitespace-pre-line`}>{heading}</p>
          <p className={`${ROW_SUB} mt-0.5`}>
            {collector.m2} m² · {isMulti ? `${collector.segments!.length} PO lines` : collector.system}
          </p>
        </div>
        <div className={ROW_AMOUNT_COL}>
          <p className={`${ROW_AMOUNT} leading-tight`}>{formatEuro(collector.amount)}</p>
          {!isMulti && collector.rate > 0 && (
            <p className={ROW_RATE}>{collector.m2} × {formatEuro(collector.rate)}</p>
          )}
        </div>
        {canTap ? (
          // A camera, not a chevron: this opens the collector's photos, it does not
          // navigate away from the statement.
          <svg className="w-4 h-4 text-td-light shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 0 1 2-2h1.5l1-2h7l1 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
            <circle cx="12" cy="13" r="3.2" />
          </svg>
        ) : (
          <div className={ROW_CHEVRON_SLOT} aria-hidden="true" />
        )}
      </div>
      {isMulti && collector.segments && (
        <div className="mt-1.5 ml-2 pl-2 border-l-2 border-slate-100 space-y-1">
          {collector.segments.map((seg, i) => (
            <div key={i} className="text-[11px] lg:text-xs text-td-muted">
              <div className="flex items-center justify-between">
                <span>{seg.system} · {seg.m2} × {formatEuro(seg.rate)}</span>
                <span className="tabular-nums">{formatEuro(seg.amount)}</span>
              </div>
              <LineDescription text={seg.description} />
            </div>
          ))}
        </div>
      )}
    </>
  );

  // The flag control sits outside the tap-through button on purpose — a button
  // inside a button is invalid markup and the inner one stops working on iOS.
  const body = canTap ? (
    <button
      type="button"
      onClick={() => onTap(collector)}
      className={`w-full ${ROW_PAD} text-left active:bg-slate-50 lg:hover:bg-slate-50 block`}
    >
      {inner}
    </button>
  ) : (
    <div className={ROW_PAD}>{inner}</div>
  );

  return (
    <div className={ROW_SHELL}>
      <div className={ROW_BODY}>{body}</div>
      <FlagStrip
        melding={melding}
        onOpen={() => onFlag({ poLineId: collector.poLineId, label: heading.split('\n')[0]!.trim() })}
      />
    </div>
  );
}

function MeerwerkRow({ item, melding, onFlag }: { item: MijnWerkMeerwerk } & RowFlagProps) {
  // Extra work is the one row type carrying prose the crew wrote themselves. Category
  // and money share one line; the text gets the full width beneath it, so a large
  // amount cannot squeeze the sentence into a narrow column.
  return (
    <div className={ROW_SHELL}>
      <div className={ROW_BODY}>
      <div className={ROW_PAD}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[12px] lg:text-[13px] lg:flex-1 lg:min-w-0 font-bold text-td-purple">{MEERWERK_LABELS[item.type] ?? item.type}</p>
        <div className={ROW_AMOUNT_COL}>
          <span className={ROW_AMOUNT}>{formatEuro(item.amount)}</span>
          <span className={`${ROW_RATE} ml-2 lg:ml-0 lg:block`}>{item.quantity} × {formatEuro(item.unitPrice)}</span>
        </div>
        <div className={ROW_CHEVRON_SLOT} aria-hidden="true" />
      </div>
      <p className="text-[13px] lg:text-sm break-words leading-snug whitespace-pre-line mt-1">{item.label}</p>
      </div>
      </div>
      <FlagStrip
        melding={melding}
        onOpen={() => onFlag({ poLineId: item.poLineId, label: item.label.split('\n')[0]!.trim() })}
      />
    </div>
  );
}

function MinprijsRow({ item, melding, onFlag }: { item: MijnWerkMinprijs } & RowFlagProps) {
  return (
    <div className={`bg-rose-50/50 ${ROW_SHELL}`}>
      <div className={`flex items-center gap-2 ${ROW_PAD} ${ROW_BODY}`}>
      <div className="min-w-0 flex-1">
        <p className={`${ROW_TITLE} leading-tight text-rose-700`}>Deduction — already placed</p>
        <p className="text-[11px] lg:text-xs text-rose-400">
          {item.count} collector{item.count !== 1 ? 's' : ''} placed by others
        </p>
        <LineDescription text={item.description} />
      </div>
      <div className={ROW_AMOUNT_COL}>
        <p className={`${ROW_AMOUNT} leading-tight text-rose-600`}>{formatEuro(item.amount)}</p>
        <p className="text-[10px] lg:text-[11px] text-rose-300 tabular-nums">{item.count} × {formatEuro(item.unitPrice)}</p>
      </div>
      <div className={ROW_CHEVRON_SLOT} aria-hidden="true" />
      </div>
      <FlagStrip
        melding={melding}
        onOpen={() => onFlag({ poLineId: item.poLineId, label: 'Deduction — already placed' })}
      />
    </div>
  );
}

function OtherRow({ item, melding, onFlag }: { item: MijnWerkOther } & RowFlagProps) {
  return (
    <div className={ROW_SHELL}>
      <div className={`flex items-center gap-2 ${ROW_PAD} ${ROW_BODY}`}>
      <div className="min-w-0 flex-1">
        <p className={`${ROW_TITLE} leading-tight break-words whitespace-pre-line`}>{item.label}</p>
        <p className={ROW_SUB}>Other PO line</p>
      </div>
      <div className={ROW_AMOUNT_COL}>
        <p className={`${ROW_AMOUNT} leading-tight`}>{formatEuro(item.amount)}</p>
        {item.quantity > 0 && (
          <p className={ROW_RATE}>{item.quantity} × {formatEuro(item.unitPrice)}</p>
        )}
      </div>
      <div className={ROW_CHEVRON_SLOT} aria-hidden="true" />
      </div>
      <FlagStrip
        melding={melding}
        onOpen={() => onFlag({ poLineId: item.poLineId, label: item.label.split('\n')[0]!.trim() })}
      />
    </div>
  );
}

/**
 * The heading of one site's card.
 *
 * On desktop it sticks directly under the day bar (`h-9`, hence `top-9`). The day
 * used to stay in view while the site scrolled away, which is the wrong half: it
 * left rows reading "C6 · Hoofdgebouw/+03" with a date and no site. Now both stay,
 * and neither has to repeat the other. The card cannot carry `overflow-hidden` for
 * this to work, so the row list below owns the bottom rounding instead.
 */
function CardHeading({ children, tone }: {
  tone: 'werf' | 'meerwerk';
  children: React.ReactNode;
}) {
  return (
    <p className={`rounded-t-2xl bg-white px-3 pt-2.5 pb-1.5 lg:px-4 lg:pt-3 lg:pb-2 text-[14px] lg:text-base font-bold leading-snug lg:sticky lg:top-9 lg:z-[5] ${
      tone === 'werf' ? 'text-td-blue' : 'text-td-purple'
    }`}>
      {children}
    </p>
  );
}

function WerfCard({ group, onCollectorTap, meldingen, onFlag }: {
  group: MijnWerkGroup;
  onCollectorTap: (collector: MijnWerkCollector, projectName: string) => void;
  meldingen: MijnWerkMelding[];
  onFlag: (target: FlagTarget) => void;
}) {
  const { werf, collectors, meerwerken, minprijs, other } = group;
  const hasItems = collectors.length > 0 || minprijs.length > 0 || other.length > 0;

  return (
    <div className="space-y-2">
      {hasItems && (
        <div className="rounded-2xl bg-white shadow-sm border border-slate-100">
          <CardHeading tone="werf">
            {werf.projectName}
            {werf.sNumber && <span className="text-[12px] lg:text-[13px] text-td-light font-normal"> · {werf.sNumber}</span>}
          </CardHeading>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-b-2xl">
            {collectors.map((c) => (
              <CollectorRow
                key={c.collectorId}
                collector={c}
                tappable={werf.tappable}
                onTap={(col) => onCollectorTap(col, werf.projectName)}
                melding={meldingForLine(meldingen, c.poLineId)}
                onFlag={onFlag}
              />
            ))}
            {minprijs.map((mp, i) => (
              <MinprijsRow key={i} item={mp} melding={meldingForLine(meldingen, mp.poLineId)} onFlag={onFlag} />
            ))}
            {other.map((o, i) => (
              <OtherRow key={i} item={o} melding={meldingForLine(meldingen, o.poLineId)} onFlag={onFlag} />
            ))}
          </div>
        </div>
      )}
      {meerwerken.length > 0 && (
        <div className="rounded-2xl bg-white shadow-sm border border-slate-100">
          <CardHeading tone="meerwerk">
            Extra work<span className="text-[12px] lg:text-[13px] text-td-light font-normal"> · {werf.projectName}</span>
          </CardHeading>
          <div className="divide-y divide-slate-100 overflow-hidden rounded-b-2xl">
            {meerwerken.map((mw, i) => (
              <MeerwerkRow key={i} item={mw} melding={meldingForLine(meldingen, mw.poLineId)} onFlag={onFlag} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MijnWerkDaySection({
  date, dayTotalM2, dayTotal, groups, onCollectorTap, meldingen, onFlag,
}: {
  date: string | null;
  dayTotalM2: number;
  dayTotal: number;
  groups: MijnWerkGroup[];
  onCollectorTap: (collector: MijnWerkCollector, projectName: string) => void;
  meldingen: MijnWerkMelding[];
  onFlag: (target: FlagTarget) => void;
}) {
  return (
    // On desktop a day is a panel with the sites sitting inside it, so which
    // sites belong to which day is structural — the date is written once, at the
    // top of the panel, rather than repeated on every card. The phone keeps the
    // plain heading it already had; nesting a tinted panel there would only eat
    // width.
    <div className="lg:rounded-2xl lg:border lg:border-slate-200 lg:bg-slate-100 lg:px-2 lg:pb-2">
      {/* The day heading sticks at the top of the viewport, and each site heading
          sticks right under it at `top-9`, so a row is never read without both. */}
      <div className="flex items-center justify-between mb-1.5 lg:sticky lg:top-0 lg:z-10 lg:bg-slate-100 lg:h-9 lg:mb-2 lg:-mx-2 lg:px-3 lg:rounded-t-2xl lg:border-b lg:border-slate-200">
        <h2 className="text-[13px] lg:text-sm font-extrabold uppercase tracking-wide text-td-navy">
          {formatDayHeader(date)}
        </h2>
        <span className="text-[11px] lg:text-xs lg:tabular-nums text-td-muted">
          {dayTotalM2 > 0 && `${dayTotalM2} m² · `}{formatEuro(dayTotal)}
        </span>
      </div>
      <div className="space-y-2">
        {groups.map((g, i) => (
          <WerfCard
            key={`${date}-${g.werf.projectId ?? i}`}
            group={g}
            onCollectorTap={onCollectorTap}
            meldingen={meldingen}
            onFlag={onFlag}
          />
        ))}
      </div>
    </div>
  );
}

// ── Report dialog ─────────────────────────────────────────────────────────────

/**
 * Where the OA writes what is wrong with one line.
 *
 * Centred rather than a bottom sheet, and built for a phone held on a werf:
 *
 *  - the overlay itself scrolls (`overflow-y-auto` + `min-h-full` centring), so
 *    when the on-screen keyboard shrinks the viewport the dialog can still be
 *    scrolled to instead of being trapped behind the keys
 *  - the textarea is 16px. Anything smaller makes iOS Safari zoom the page in
 *    on focus and it never zooms back out, which leaves the crew pinching
 *    around a half-visible form
 *  - the text survives a failed send. This is typed with cold hands on a bad
 *    connection, and losing three sentences is how a feature stops being used.
 */
function MeldingSheet({ target, existing, onClose, onSubmit }: {
  target: FlagTarget;
  existing?: MijnWerkMelding;
  onClose: () => void;
  onSubmit: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFollowUp = existing?.status === 'open';

  // Escape closes, and the list behind the dialog stays put — on a phone a
  // scrolling background under a modal loses the OA's place in a long PO.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (err) {
      // A 403 carries a code but no message, so err.message is ''. Rendering that
      // shows nothing at all and Send silently does nothing — say something.
      setError(err instanceof Error && err.message ? err.message : 'Sending failed');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[61] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Report an issue">
      <button type="button" className="fixed inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      {/* Extra bottom clearance, not symmetric padding: on a short viewport (an
          open keyboard) a centred dialog puts Send within a few pixels of the
          fixed bottom nav. The overlay scrolls, so this buys the room to clear
          it; at full height it only lifts the dialog slightly above centre. */}
      <div className="relative flex min-h-full items-center justify-center px-4 pt-4 pb-20">
        <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-xl space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-td-muted font-bold">
              {isFollowUp ? 'Add to your report' : 'Report an issue'}
            </p>
            <p className="text-[13px] font-semibold text-td-navy leading-snug mt-0.5 break-words">
              {target.label}
            </p>
          </div>

          {existing && (
            <div className="rounded-lg bg-slate-50 px-2 py-1.5 max-h-32 overflow-y-auto">
              <p className="text-[11px] text-td-muted whitespace-pre-line leading-snug">{existing.text}</p>
              {existing.resolution && (
                <p className="text-[11px] text-emerald-800 leading-snug mt-1">Office: {existing.resolution}</p>
              )}
            </div>
          )}

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={1000}
            autoFocus
            placeholder="What is wrong with this line?"
            className="w-full rounded-xl border border-slate-200 p-2.5 text-[16px] leading-snug focus:outline-none focus:border-td-navy"
          />

          {error && <p className="text-[12px] text-rose-600">{error}</p>}

          {/* What it does for them, not how it works. The to-do, the purchase order
              line and the chatter copy are ours to worry about, not theirs. */}
          <p className="text-[11px] text-td-light leading-snug">
            Thermoduct is notified straight away. You will see it here once it has been handled.
          </p>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl bg-slate-100 py-3 min-h-[44px] text-[13px] font-bold text-td-navy active:bg-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={send}
              disabled={!text.trim() || sending}
              className="flex-1 rounded-xl bg-td-navy py-3 min-h-[44px] text-[13px] font-bold text-white active:opacity-80 disabled:opacity-40"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Collector photos ──────────────────────────────────────────────────────────

/** What a photo request needs, gathered from the row that was tapped. */
export interface PhotoTarget {
  collector: MijnWerkCollector;
  projectName: string;
}

/**
 * The photos of one collector, opened from its row.
 *
 * Tapping a collector used to leave the statement for the project page. That is
 * the wrong destination when the question is "did we photograph this one?" — the
 * crew loses their place in a forty-line purchase order to look at something they
 * then have to navigate back from. This stays on top of the statement instead.
 *
 * Scoping is the server's: the endpoint refuses a collector on a project the
 * caller is not on, which is the same condition that makes a row untappable, so a
 * refusal here is a bug rather than an expected state — it is shown as an error
 * and not dressed up as "no photos".
 */
function CollectorPhotoSheet({ target, onClose }: { target: PhotoTarget; onClose: () => void }) {
  const { collector, projectName } = target;
  const [photos, setPhotos] = useState<PhotoItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setPhotos(null);
    setError(null);
    // The server resolves the real storage prefix from Odoo; this is the legacy
    // fallback it accepts alongside it.
    listCollectorPhotos(collector.collectorId, {
      projectName,
      blok: collector.blok || 'Hoofdgebouw',
      verdieping: collector.verdieping || '+00',
      collectorName: collector.label.replace(/\s+/g, '-'),
    })
      .then((res) => { if (!cancelled) setPhotos(res.photos); })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error && err.message ? err.message : 'Could not load the photos');
      });
    return () => { cancelled = true; };
  }, [collector, projectName]);

  // The lightbox is an image viewer; a PDF (a drukattest) opens in its own tab.
  const imagePhotos = (photos ?? []).filter((p) => !isPdfPhoto(p));

  return (
    <div className="fixed inset-0 z-[61] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Photos">
      <button type="button" className="fixed inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative flex min-h-full items-start justify-center px-4 pt-4 pb-20 lg:items-center lg:py-10">
        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
          <div className="flex items-start gap-3 border-b border-slate-100 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wide text-td-muted font-bold">Photos</p>
              <p className="text-[15px] font-bold text-td-navy leading-snug break-words">
                {collector.label} · {collector.blok} / {collector.verdieping}
              </p>
              <p className="text-[11px] text-td-light break-words">{projectName}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-xl bg-slate-100 px-3 py-2 min-h-[44px] text-[13px] font-bold text-td-navy active:bg-slate-200 lg:hover:bg-slate-200"
            >
              Close
            </button>
          </div>

          <div className="p-4">
            {!photos && !error && (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="aspect-square rounded-lg bg-slate-100 animate-pulse" />
                ))}
              </div>
            )}

            {error && (
              <div className="rounded-xl bg-red-50 p-3 text-red-600" role="alert">
                <p className="text-[13px] font-semibold">Could not load the photos</p>
                <p className="text-[12px] mt-0.5">{error}</p>
              </div>
            )}

            {photos && !error && photos.length === 0 && (
              <p className="py-8 text-center text-[13px] text-td-muted">
                No photos for this collector yet.
              </p>
            )}

            {photos && photos.length > 0 && (
              <>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {photos.map((photo) => (
                    <PhotoGridThumb
                      key={photo.key}
                      photo={photo}
                      imgClassName="w-full aspect-square object-cover rounded-lg"
                      onOpen={() => {
                        const i = imagePhotos.findIndex((p) => p.key === photo.key);
                        if (i >= 0) setLightboxAt(i);
                        else window.open(photo.url, '_blank', 'noopener');
                      }}
                      openLabel={`Open ${photo.filename}`}
                    />
                  ))}
                </div>
                <p className="mt-3 text-[11px] text-td-light">
                  {photos.length} photo{photos.length !== 1 ? 's' : ''}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {lightboxAt !== null && imagePhotos.length > 0 && (
        <PhotoLightbox
          photos={imagePhotos}
          initialIndex={lightboxAt}
          onClose={() => setLightboxAt(null)}
        />
      )}
    </div>
  );
}

// ── PO picker bottom sheet ────────────────────────────────────────────────────
// One PO = one billing period, so this replaces the old date-range picker: the OA
// scrolls their own purchase orders instead of guessing which dates were invoiced.

function PoPickerSheet({
  pos, currentPoId, onSelect, onClose,
}: {
  pos: MijnWerkPoRef[];
  currentPoId: number | null;
  onSelect: (poId: number) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/30" onClick={onClose} aria-hidden="true" />
      <div className="fixed inset-x-0 bottom-0 z-[61] rounded-t-3xl bg-white shadow-2xl px-4 pt-3 pb-8 max-h-[75vh] flex flex-col">
        <div className="mx-auto w-10 h-1.5 rounded-full bg-slate-200 mb-4 shrink-0" />
        <h2 className="text-base font-extrabold mb-3 shrink-0">Choose purchase order</h2>

        <div className="space-y-2 overflow-y-auto">
          {pos.map((po) => (
            <button
              key={po.id}
              type="button"
              onClick={() => { onSelect(po.id); onClose(); }}
              className={`w-full flex items-center justify-between rounded-xl border-2 px-3 py-3 text-left ${
                po.id === currentPoId ? 'border-td-blue bg-td-blue/5' : 'border-slate-200 active:bg-slate-50'
              }`}
            >
              <span>
                <span className="flex items-center gap-2">
                  <span className="text-[13px] font-bold tabular-nums">{po.name}</span>
                  {po.isOpen && (
                    <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5">
                      Open
                    </span>
                  )}
                </span>
                <span className="block text-[11px] text-td-muted mt-0.5">
                  {po.isOpen ? 'Still running · not yet approved' : 'Approved'}
                  {po.startedAt ? ` · started ${formatShortDate(po.startedAt)}` : ''}
                </span>
              </span>
              <span className="text-[13px] font-bold tabular-nums ml-auto mr-2 shrink-0">
                {formatEuro(po.poTotal)}
              </span>
              {po.id === currentPoId && (
                <svg className="w-5 h-5 text-td-blue shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Statement header ──────────────────────────────────────────────────────────

/**
 * What the crew came for: the figure to invoice and the reference to quote alongside it.
 */
function StatementHeader({
  po, total, totalM2,
}: {
  po: MijnWerkPo | null;
  total: number;
  totalM2: number;
}) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 pt-3.5 pb-3">
        <p className="text-[11px] font-bold uppercase tracking-wide text-td-muted">
          {po?.isOpen ? 'Not yet approved · amount can change' : 'Approved purchase order'}
        </p>
        <p className="text-[32px] font-extrabold leading-none mt-1.5 tabular-nums text-td-navy">
          {formatEuro(total)}
        </p>
        <p className="text-[11px] text-td-muted mt-1">PO total excluding VAT</p>

        {po && (
          <div className="mt-2.5 pt-2.5 border-t border-slate-100">
            <p className="text-[15px] font-extrabold tabular-nums text-td-navy leading-tight">{po.name}</p>
            <p className="text-[11px] text-td-muted mt-0.5">
              State this on your invoice · {formatPoSpan(po)}
            </p>
          </div>
        )}
      </div>

      {totalM2 > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/60">
          <div className="flex items-baseline justify-between px-4 py-2">
            <span className="text-[12px] text-td-light">Area placed</span>
            <span className="text-[12px] text-td-light tabular-nums">{totalM2} m²</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Desktop rail cards ────────────────────────────────────────────────────────

/**
 * The PO switcher as a permanent list. A bottom sheet is the right control on a
 * phone; on desktop there is room to keep every purchase order in view, so
 * switching billing period costs one click instead of three.
 */
function PoRail({ pos, currentPoId, onSelect }: {
  pos: MijnWerkPoRef[];
  currentPoId: number | null;
  onSelect: (poId: number) => void;
}) {
  if (pos.length === 0) return null;
  return (
    <div className="hidden lg:block rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
      <p className="px-4 pt-3 pb-2 text-[11px] font-bold uppercase tracking-wide text-td-muted">
        Purchase orders
      </p>
      <div className="divide-y divide-slate-100">
        {pos.map((po) => (
          <button
            key={po.id}
            type="button"
            onClick={() => onSelect(po.id)}
            aria-current={po.id === currentPoId ? 'true' : undefined}
            className={`w-full flex items-center gap-2 px-4 py-2.5 text-left border-l-4 hover:bg-slate-50 ${
              po.id === currentPoId ? 'border-td-blue bg-td-blue/5' : 'border-transparent'
            }`}
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-bold tabular-nums text-td-navy break-words">{po.name}</span>
              <span className="block text-[11px] text-td-light">
                {po.startedAt ? formatShortDate(po.startedAt) : 'No start date'}
              </span>
            </span>
            {po.isOpen && (
              <span className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5 shrink-0">
                Open
              </span>
            )}
            <span className="text-xs font-bold tabular-nums text-td-navy shrink-0">
              {formatEuro(po.poTotal)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

export function InstallerWerkView() {
  const { data, pos, selectedPoId, loading, error, fetchData, selectPo, submitMelding } = useMijnWerkStore();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [flagTarget, setFlagTarget] = useState<FlagTarget | null>(null);
  const [photoTarget, setPhotoTarget] = useState<PhotoTarget | null>(null);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCollectorTap = useCallback((collector: MijnWerkCollector, projectName: string) => {
    setPhotoTarget({ collector, projectName });
  }, []);

  const handleFlag = useCallback((target: FlagTarget) => {
    setFlagTarget(target);
  }, []);

  // The PO shown: while a switch is in flight `data` is gone, so the rail reads the
  // pending selection instead and the highlight moves on the click, not on the reply.
  const shownPoId = selectedPoId ?? data?.po?.id ?? null;

  const handlePoSelect = useCallback((poId: number) => {
    // Clicking the PO already on screen would throw the statement away and fetch
    // the same thing back.
    if (poId === shownPoId) return;
    selectPo(poId);
    fetchData();
  }, [selectPo, fetchData, shownPoId]);

  // Aggregate groups by date (one group = one date+werf tuple)
  const dayMap = new Map<string, MijnWerkGroup[]>();
  if (data) {
    for (const group of data.groups) {
      const key = group.date ?? '';
      if (!dayMap.has(key)) dayMap.set(key, []);
      dayMap.get(key)!.push(group);
    }
  }
  // The API already orders groups newest-first with the undated card last; a Map
  // preserves insertion order, so do not re-sort here.
  const dates = Array.from(dayMap.keys());

  return (
    <div className="max-w-lg md:max-w-3xl lg:max-w-6xl mx-auto lg:px-6 lg:pt-6">
      <div className="lg:grid lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-6 lg:items-start">
      {/* Statement rail. On a phone this is simply the top of the page. On desktop
          it stays put while the lines scroll, so the figure to invoice, what it is
          made of, and the purchase orders never leave the screen. */}
      {/* The rail scrolls on its own when the screen is too short to hold it —
          otherwise a laptop viewport pins it and the last purchase orders become
          unreachable. */}
      <aside className="px-4 pt-4 pb-3 lg:p-0 lg:sticky lg:top-6 lg:space-y-4 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
        <div className="flex items-center justify-between mb-3 lg:mb-0">
          <h1 className="text-lg font-extrabold text-td-navy">My work</h1>
          {/* The sheet is the phone control; desktop switches PO in the rail below. */}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="lg:hidden flex items-center gap-1 rounded-xl bg-slate-100 px-3 py-2 text-left active:bg-slate-200 min-h-[44px]"
            aria-label="Change purchase order"
            disabled={pos.length === 0}
          >
            <span>
              <span className="block text-[10px] text-td-muted leading-none">
                Purchase order{data?.po?.isOpen ? ' · open' : ''}
              </span>
              <span className="block text-sm font-bold leading-tight text-td-navy tabular-nums">
                {data?.po ? data.po.name : '—'}
              </span>
              {data?.po && (
                <span className="block text-[10px] text-td-muted leading-none mt-0.5">
                  {formatPoSpan(data.po)}
                </span>
              )}
            </span>
            <svg className="w-4 h-4 text-td-muted ml-1 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {data && data.isOa && (
          <>
            <StatementHeader total={data.total} totalM2={data.totalM2} po={data.po} />
          </>
        )}
        {!data && loading && pos.length > 0 && (
          <div className="hidden lg:block rounded-2xl bg-white border border-slate-200 shadow-sm px-4 py-3.5 animate-pulse">
            <div className="h-2.5 w-2/3 rounded bg-slate-200" />
            <div className="h-8 w-1/2 rounded bg-slate-100 mt-2.5" />
            <div className="h-2.5 w-1/3 rounded bg-slate-100 mt-3" />
            <div className="h-px bg-slate-100 my-3" />
            <div className="h-3.5 w-1/3 rounded bg-slate-100" />
            <div className="h-2.5 w-2/3 rounded bg-slate-100 mt-2" />
            <div className="h-2.5 w-1/4 rounded bg-slate-100 mt-4" />
          </div>
        )}
        <PoRail pos={pos} currentPoId={shownPoId} onSelect={handlePoSelect} />
      </aside>

      <div className="lg:min-w-0">
      {/* Loading skeletons */}
      {loading && !data && (
        <div className="px-4 lg:px-0 space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
              <div className="h-3 bg-slate-200 rounded w-1/3 mb-3" />
              <div className="h-10 bg-slate-100 rounded mb-2" />
              <div className="h-10 bg-slate-100 rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-4 lg:mx-0 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Could not load work overview</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            type="button"
            onClick={() => fetchData()}
            className="mt-2 text-sm font-bold text-red-700 underline min-h-[44px] flex items-center"
          >
            Try again
          </button>
        </div>
      )}

      {/* Not an OA */}
      {!loading && !error && data && !data.isOa && (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l2 2 4-4M5 7h14l-1 13a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7zM9 7V5a3 3 0 016 0v2" />
            </svg>
          </div>
          <p className="font-bold text-td-navy">Not available</p>
          <p className="text-sm text-td-muted mt-1">This overview is for subcontractors only.</p>
        </div>
      )}

      {/* Empty period */}
      {!loading && !error && data && data.isOa && data.groups.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
          <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-td-muted text-sm font-medium">
            {data.po ? 'No work on this purchase order yet' : 'No purchase orders yet'}
          </p>
        </div>
      )}

      {/* Day groups */}
      {!loading && !error && data && data.isOa && dates.length > 0 && (
        <div className="px-4 lg:px-0 pb-28 lg:pb-10 space-y-4 lg:space-y-6">
          {dates.map((date) => {
            const dayGroups = dayMap.get(date)!;
            const dayTotalM2 = dayGroups.reduce((s, g) => s + g.dayTotalM2, 0);
            const dayTotal = dayGroups.reduce((s, g) => s + g.dayTotal, 0);
            return (
              <MijnWerkDaySection
                key={date || 'undated'}
                date={date || null}
                dayTotalM2={dayTotalM2}
                dayTotal={dayTotal}
                groups={dayGroups}
                onCollectorTap={handleCollectorTap}
                meldingen={data.meldingen ?? []}
                onFlag={handleFlag}
              />
            );
          })}
          <p className="text-center text-[10px] text-td-light pt-1">
            End of {data.po ? data.po.name : 'purchase order'}
          </p>
        </div>
      )}
      </div>
      </div>

      {photoTarget && (
        <CollectorPhotoSheet target={photoTarget} onClose={() => setPhotoTarget(null)} />
      )}

      {flagTarget && (
        <MeldingSheet
          target={flagTarget}
          existing={data ? meldingForLine(data.meldingen ?? [], flagTarget.poLineId) : undefined}
          onClose={() => setFlagTarget(null)}
          onSubmit={(text) => submitMelding(flagTarget.poLineId, text)}
        />
      )}

      {/* PO picker bottom sheet */}
      {pickerOpen && pos.length > 0 && (
        <PoPickerSheet
          pos={pos}
          currentPoId={shownPoId}
          onSelect={handlePoSelect}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* Sticky PO total — sits above InstallerBottomNav (fixed bottom-0, ~56px).
          Hidden on desktop where the bottom nav is also hidden (lg:hidden). */}
      {data && data.isOa && (
        <div className="fixed bottom-[var(--nav-bottom-h)] left-0 right-0 z-40 bg-white border-t border-slate-200 px-4 py-2.5 flex items-center justify-between lg:hidden">
          <span className="text-[12px] font-semibold text-td-muted tabular-nums">
            {data.po ? data.po.name : 'PO'}
            <span className="font-normal text-td-light"> · {data.po?.isOpen ? 'not approved' : 'approved'}</span>
          </span>
          <span className="text-base font-extrabold tabular-nums">{formatEuro(data.total)}</span>
        </div>
      )}
    </div>
  );
}
